import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";
import { parseThemeApiResponse, siteThemeAdminUrl } from "../ThemeManagement/themeApiUtils";
import ThemeInstallWizard from "./ThemeInstallWizard";

const AppearanceThemes = () => {
  const urlmain = API_BASE_URL;
  const navigate = useNavigate();
  const token = decryptToken("accessToken") || "";
  const [catalog, setCatalog] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizardTheme, setWizardTheme] = useState(null);
  const [wizardInstallation, setWizardInstallation] = useState(null);

  const thumbUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${urlmain}${path}`;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [catalogRes, installedRes] = await Promise.all([
        fetch(siteThemeAdminUrl("/catalog"), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(siteThemeAdminUrl("/installed"), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const catalogJson = await parseThemeApiResponse(catalogRes);
      const installedJson = await parseThemeApiResponse(installedRes);
      if (catalogRes.ok && !catalogJson.isError) setCatalog(catalogJson.data || []);
      if (installedRes.ok && !installedJson.isError) setInstalled(installedJson.data || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const isInstalled = (themeId) => installed.some((row) => row.theme_id === themeId);
  const getInstallation = (themeId) => installed.find((row) => row.theme_id === themeId);

  const openWizard = (theme, installation = null) => {
    setWizardTheme(theme);
    setWizardInstallation(installation);
  };

  const updateTheme = async (installationId) => {
    try {
      const response = await fetch(siteThemeAdminUrl(`/${installationId}/update`), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        toast.success("Theme updated");
        loadData();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const needsOnboarding = (installation) =>
    installation && (!installation.onboarding_completed || !installation.is_active);

  const getStatusLabel = (installation) => {
    if (!installation) return null;
    if (installation.is_active && installation.onboarding_completed) {
      return { text: "Active", color: "#059669" };
    }
    if (installation.is_active) {
      return { text: "Active — finish setup", color: "#d97706" };
    }
    if (installation.domain_verified) {
      return { text: "Verified — activate to go live", color: "#d97706" };
    }
    return { text: "Installed — setup incomplete", color: "#d97706" };
  };

  return (
    <>
      <div className="oms-header">
        <h1 className="dashboard-title">Themes</h1>
      </div>

      <p style={{ marginBottom: 16, color: "#666" }}>
        Install a theme, connect your domain (DNS), verify, activate, then customize your storefront.
      </p>

      {loading ? (
        <p>Loading themes...</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {catalog.map((theme) => {
            const installation = getInstallation(theme.id);
            const statusLabel = getStatusLabel(installation);
            return (
              <div
                key={theme.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                {theme.thumbnail && (
                  <img
                    src={thumbUrl(theme.thumbnail)}
                    alt={theme.name}
                    style={{ width: "100%", height: 140, objectFit: "cover" }}
                  />
                )}
                <div style={{ padding: 16 }}>
                  <h3 style={{ margin: "0 0 8px" }}>{theme.name}</h3>
                  <p style={{ fontSize: 13, color: "#666", minHeight: 40 }}>
                    {theme.description || "—"}
                  </p>
                  <p style={{ fontSize: 12, color: "#888" }}>
                    Version: {installation?.installed_version || theme.version}
                    {statusLabel && (
                      <span style={{ color: statusLabel.color, marginLeft: 8 }}>
                        {statusLabel.text}
                      </span>
                    )}
                    {installation?.update_available && (
                      <span style={{ color: "#d97706", marginLeft: 8 }}>
                        Update to {installation.latest_version}
                      </span>
                    )}
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                    {!isInstalled(theme.id) && (
                      <button type="button" className="oms-add-btn" onClick={() => openWizard(theme)}>
                        Install & set up
                      </button>
                    )}
                    {isInstalled(theme.id) && needsOnboarding(installation) && (
                      <button
                        type="button"
                        className="oms-add-btn"
                        onClick={() => openWizard(theme, installation)}
                      >
                        {installation?.domain_verified && !installation?.is_active
                          ? "Activate theme"
                          : "Continue setup"}
                      </button>
                    )}
                    {installation?.is_active && (
                      <>
                        <span style={{ color: "#059669", fontSize: 13, alignSelf: "center" }}>Active</span>
                        <button type="button" onClick={() => navigate("/appearance/customize")}>
                          Customize
                        </button>
                      </>
                    )}
                    {installation?.update_available && (
                      <button type="button" className="oms-add-btn" onClick={() => updateTheme(installation.id)}>
                        Update theme
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {wizardTheme && (
        <ThemeInstallWizard
          token={token}
          theme={wizardTheme}
          installation={wizardInstallation}
          onClose={() => {
            setWizardTheme(null);
            setWizardInstallation(null);
            loadData();
          }}
          onComplete={loadData}
        />
      )}
    </>
  );
};

export default AppearanceThemes;
